import React, { useState } from 'react';
import { Shield, Sword, Scroll, Coins, Crown } from 'lucide-react';
import { isValidSolanaAddress } from '../../utils/validation';
import { useSupabaseLeaderboard } from '../../hooks/useSupabaseLeaderboard';
import { generateGuestUsername } from '../../utils/guestUtils';

interface UserRegistrationProps {
  onRegister: (username: string, walletAddress: string) => void;
  onCancel: () => void;
}

export function UserRegistration({ onRegister, onCancel }: UserRegistrationProps) {
  const [username, setUsername] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const { addUser } = useSupabaseLeaderboard();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('A name befitting a warrior is required');
      return;
    }
    if (!walletAddress.trim()) {
      setError('Your treasury vault address is required');
      return;
    }
    if (!isValidSolanaAddress(walletAddress)) {
      setError('Please provide a valid Solana treasury address');
      return;
    }

    setIsRegistering(true);
    setError(null);

    try {
      await addUser(username.trim(), walletAddress.trim(), 30000);
      onRegister(username.trim(), walletAddress.trim());
    } catch (err) {
      setError('Failed to register. Please try again.');
      setIsRegistering(false);
    }
  };

  const handleGuestLogin = async () => {
    setIsRegistering(true);
    setError(null);
    try {
      const guestUsername = generateGuestUsername();
      const guestWallet = `guest-${Date.now()}`;
      await addUser(guestUsername, guestWallet, 30000);
      onRegister(guestUsername, guestWallet);
    } catch (err) {
      setError('Failed to create guest account. Please try again.');
      setIsRegistering(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[url('https://images.unsplash.com/photo-1533035353720-f1c6a75cd8ab?auto=format&fit=crop&q=80')] bg-cover bg-center">
      <div className="fixed inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/80 backdrop-blur-sm">
        <div className="absolute inset-0 opacity-30">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-amber-300 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 5}s`
              }}
            />
          ))}
        </div>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-amber-600/20 to-yellow-500/20 p-0.5 rounded-xl max-w-md w-full backdrop-blur-lg">
            <div className="bg-gray-900/90 p-8 rounded-lg relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent" />
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent" />
              
              <div className="text-center mb-8">
                <div className="flex justify-center items-center gap-4 mb-6">
                  <Shield className="w-8 h-8 text-yellow-500 animate-pulse" />
                  <Crown className="w-8 h-8 text-yellow-500 animate-pulse-slow" />
                  <Sword className="w-8 h-8 text-yellow-500 animate-pulse" />
                </div>
                <h2 className="text-3xl font-bold font-serif text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-500 mb-2">
                  Access to the Kingdom
                </h2>
                <p className="text-amber-200/80 text-sm font-serif mb-1">
                  State thy name and treasury, brave soul
                </p>
                <div className="mt-2 text-amber-400/60 text-xs font-serif italic border-t border-amber-400/20 pt-2 max-w-[200px] mx-auto">
                  Solstice CA: FgWkUB42PL8y4Xw6XPcaMdGJeuW9fhjKsVDWg7YDpump

                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="username" className="block text-sm font-serif text-amber-200/80 mb-2">
                    Declare Thy Name
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Sir Lancelot"
                    disabled={isRegistering}
                    className="w-full px-4 py-3 bg-gray-800/80 border border-amber-700/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500/50 text-amber-100 placeholder-amber-400/50 disabled:opacity-50"
                  />
                </div>

                <div>
                  <label htmlFor="wallet" className="block text-sm font-serif text-amber-200/80 mb-2">
                    Treasury Vault Address
                  </label>
                  <input
                    type="text"
                    id="wallet"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Enter your Solana address"
                    disabled={isRegistering}
                    className="w-full px-4 py-3 bg-gray-800/80 border border-amber-700/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500/50 text-amber-100 font-mono text-sm placeholder-amber-400/50 disabled:opacity-50"
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-sm font-serif text-center">{error}</p>
                )}

                <div className="flex flex-col gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={isRegistering}
                    className="w-full px-6 py-3 bg-gradient-to-r from-yellow-600 to-amber-500 text-white rounded-lg hover:from-yellow-500 hover:to-amber-400 transition-all font-serif disabled:opacity-50 transform hover:scale-105"
                  >
                    {isRegistering ? 'Entering...' : 'Enter Kingdom'}
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-amber-700/30"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-900/90 text-amber-200/80">Or</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGuestLogin}
                    disabled={isRegistering}
                    className="w-full px-6 py-3 bg-gray-800/80 text-amber-200/80 rounded-lg hover:bg-gray-700/80 transition-all font-serif disabled:opacity-50"
                  >
                    Play as Guest
                  </button>

                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isRegistering}
                    className="w-full px-6 py-3 bg-gray-800/50 text-amber-200/70 rounded-lg hover:bg-gray-700/50 transition-all font-serif disabled:opacity-50"
                  >
                    Retreat
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}